import React, {useEffect} from 'react';

import useLayout from '@/hooks/layout/useLayout';
import amplitudeHomePageTracking from '@/services/analytics/amplitude/amplitudeHomePageTracking';
import googleAnalyticsService from '@/services/analytics/ga/googleAnalyticsService';
import {useDynamicDesktopHighlights} from '@/views/Highlights/components/DesktopHighlights/hooks/useDynamicDesktopHighlights';

import {useHighlightsSectionId} from '../../contexts/HighlightsSectionIdContext';
import useHighlightsPushNotification from '../../hooks/useHighlightsPushNotification';
import useHighlightsState from '../../hooks/useHighlightsState';
import HighlightsContainer from '../common/components/HighlightsContainer';
import HighlightsHeader from '../common/components/HighlightsHeader';
import FullscreenDesktopHighlights from '../FullscreenDesktopHighlights/FullscreenDesktopHighlights';
import FullscreenHighlightsCarousel from '../MobileHighlights/components/FullscreenHighlightsCarousel';
import HighlightsCarouselList from './components/HighlightsCarouselList';
import {findCarouselAndSlideByTag, getHighlights} from './services/highlightsService';

const DesktopHighlights = () => {
    const {isMd, isMl} = useLayout();
    const id = useHighlightsSectionId();
    const {isFullscreen, setIsFullscreen, currentIndex, setCurrentIndex} = useHighlightsState();

    const {carouselsFromConfig, isAutoPlayEnabled} = getHighlights(id);
    const carouselsFiltered = useDynamicDesktopHighlights(carouselsFromConfig);

    const visibleCount = isMd ? 2 : isMl ? 3 : carouselsFiltered.length;

    const {shouldOpenFullscreen, globalIndex, carouselIndex, slideIndex, notificationTrigger} =
        useHighlightsPushNotification((tag) => findCarouselAndSlideByTag(carouselsFiltered, visibleCount, tag));

    // Open fullscreen when triggered by push notification
    useEffect(() => {
        if (shouldOpenFullscreen) {
            setCurrentIndex(globalIndex);
            setIsFullscreen(true);
        }
    }, [notificationTrigger, shouldOpenFullscreen, globalIndex, setCurrentIndex, setIsFullscreen]);

    if (!carouselsFiltered?.length) return null;

    const handleViewAllClick = () => {
        amplitudeHomePageTracking.trackHighlightViewAllClick();
        googleAnalyticsService.trackHighlightsViewAllClick();
        setIsFullscreen(true);
    };

    return (
        <HighlightsContainer className='@container'>
            <HighlightsHeader onViewAllClick={handleViewAllClick} />
            <HighlightsCarouselList
                carouselsFromConfig={carouselsFiltered}
                visibleCount={visibleCount}
                isAutoPlayEnabled={isAutoPlayEnabled}
                isPlaying={!isFullscreen}
            />
            {isFullscreen && (
                <FullscreenHighlights
                    isMobileView={isMd}
                    carouselsFromConfig={carouselsFiltered}
                    visibleCount={visibleCount}
                    isAutoPlayEnabled={isAutoPlayEnabled}
                    setIsFullscreen={setIsFullscreen}
                    initialHighlightIndex={currentIndex}
                    initialCarouselIndex={carouselIndex}
                    initialSlideIndex={slideIndex}
                />
            )}
        </HighlightsContainer>
    );
};

const FullscreenHighlights = ({
    isMobileView,
    carouselsFromConfig,
    visibleCount,
    isAutoPlayEnabled,
    setIsFullscreen,
    initialHighlightIndex,
    initialCarouselIndex,
    initialSlideIndex,
}) => {
    if (isMobileView) {
        const mergedHighlights = carouselsFromConfig.slice(0, visibleCount).flatMap((carousel) => carousel.highlights);

        return (
            <FullscreenHighlightsCarousel
                setIsFullscreen={setIsFullscreen}
                currentIndex={initialHighlightIndex}
                highlights={mergedHighlights}
                autoplay={isAutoPlayEnabled}
                isInfinite
            />
        );
    }

    return (
        <FullscreenDesktopHighlights
            carouselsFromConfig={carouselsFromConfig}
            isAutoPlayEnabled={isAutoPlayEnabled}
            onClose={() => setIsFullscreen(false)}
            initialCarouselIndex={initialCarouselIndex}
            initialSlideIndex={initialSlideIndex}
        />
    );
};

export default DesktopHighlights;
